import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Loader2, Save, FileBox, Lightbulb } from "lucide-react";
import { getErrorMessage } from "../../utils/errorHandler";
import { API, TONES, LENGTHS, authHeader, sendSermonToEditor } from "./shared";
import { SkeletonOutline, EmptyState, EXAMPLE_OUTLINES, fadeIn } from "./_shared_ui";
import RegenerateButton from "./RegenerateButton";
import ExportMenu from "./ExportMenu";

// Build a stable key per section so we can mark it dirty when the user
// hand-edits inline. Point sub-fields use `${field}#${idx}`.
const dirtyKey = (section, idx = null) =>
  idx === null ? section : `${section}#${idx}`;

export default function SermonOutlinePanel({ t, onSaved }) {
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [scripture, setScripture] = useState("");
  const [tone, setTone] = useState("encouraging");
  const [length, setLength] = useState("medium");
  const [loading, setLoading] = useState(false);
  const [outline, setOutline] = useState(null);
  // Set of section keys that have been manually edited since last generation.
  const [dirty, setDirty] = useState(new Set());

  const markDirty = useCallback((section, idx = null) => {
    setDirty((prev) => {
      const k = dirtyKey(section, idx);
      if (prev.has(k)) return prev;
      const next = new Set(prev);
      next.add(k);
      return next;
    });
  }, []);

  const clearDirty = useCallback((section, idx = null) => {
    setDirty((prev) => {
      const k = dirtyKey(section, idx);
      if (!prev.has(k)) return prev;
      const next = new Set(prev);
      next.delete(k);
      return next;
    });
  }, []);

  const isDirty = (section, idx = null) => dirty.has(dirtyKey(section, idx));

  const generate = async () => {
    if (!topic && !scripture) {
      toast.error(t("provide_topic_or_scripture") || "Provide a topic, scripture, or both");
      return;
    }
    setLoading(true);
    setOutline(null);
    setDirty(new Set());
    try {
      const res = await axios.post(`${API}/api/pulpit/sermon-outline`, {
        topic: topic || null, scripture: scripture || null, tone, length,
      }, { headers: authHeader() });
      setOutline(res.data.outline);
      toast.success(t("outline_generated") || "Outline generated");
    } catch (err) {
      toast.error(getErrorMessage(err, t("generation_failed") || "Generation failed"));
    } finally { setLoading(false); }
  };

  const saveOutline = async () => {
    if (!outline) return;
    try {
      await axios.post(`${API}/api/pulpit/sermons`, {
        title: outline.title || "Untitled Sermon",
        sermon_type: "outline",
        scripture: outline.scripture_main || scripture || null,
        content: outline,
      }, { headers: authHeader() });
      toast.success(t("sermon_saved") || "Saved to library");
      // After saving, edits are persisted — drop dirty flags.
      setDirty(new Set());
      onSaved && onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, t("save_failed") || "Save failed"));
    }
  };

  const sendToEditor = () => {
    if (!outline) return;
    sendSermonToEditor({
      title: outline.title || "Sermon Outline",
      sermon_type: "outline",
      content: outline,
      navigate, t,
    });
  };

  // Helpers to edit fields while flagging them dirty.
  const setField = (key, value) => {
    setOutline((o) => ({ ...o, [key]: value }));
    markDirty(key);
  };
  const setPointField = (idx, key, value) => {
    setOutline((o) => {
      const next = [...(o.points || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...o, points: next };
    });
    markDirty(key, idx);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-base">{t("create_outline") || "Create Sermon Outline"}</CardTitle>
          <CardDescription>{t("outline_desc") || "AI generates a 3-point outline with scripture, illustrations and CTA"}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">{t("topic") || "Topic"}</Label>
            <Input data-testid="outline-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)}
              placeholder={t("topic_placeholder") || "e.g., God's faithfulness in trials"} />
          </div>
          <div>
            <Label className="text-xs text-slate-400">{t("primary_scripture") || "Primary Scripture"}</Label>
            <Input data-testid="outline-scripture-input" value={scripture} onChange={(e) => setScripture(e.target.value)}
              placeholder="e.g., Romans 8:28" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-400">{t("tone") || "Tone"}</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TONES.map((tn) => <SelectItem key={tn} value={tn}>{tn}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">{t("length") || "Length"}</Label>
              <Select value={length} onValueChange={setLength}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LENGTHS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={generate} disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="generate-outline-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {loading ? (t("generating") || "Generating...") : (t("generate_outline") || "Generate Outline")}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base">{t("result") || "Result"}</CardTitle>
            <CardDescription className="truncate">
              {outline?.title || (loading ? (t("generating") || "Generating...") : (t("no_outline_yet") || "No outline yet"))}
              {outline && dirty.size > 0 && (
                <span className="ml-2 text-[10px] text-amber-400 uppercase tracking-wide" data-testid="outline-dirty-badge">
                  • {t("edited") || "edited"}
                </span>
              )}
            </CardDescription>
          </div>
          {outline && (
            <div className="flex gap-1">
              <ExportMenu title={outline.title || "Sermon Outline"} sermonType="outline" content={outline} t={t} testId="export-outline" />
              <Button variant="outline" size="sm" onClick={sendToEditor} data-testid="send-outline-to-editor-btn" className="text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10">
                <FileBox className="w-3.5 h-3.5 mr-1" />
                {t("send_to_editor") || "Send to Editor"}
              </Button>
              <Button variant="outline" size="sm" onClick={saveOutline} data-testid="save-outline-btn">
                <Save className="w-3.5 h-3.5 mr-1" />
                {t("save") || "Save"}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px] pr-2">
            {loading ? (
              <SkeletonOutline />
            ) : !outline ? (
              <EmptyState
                icon={Lightbulb}
                title={t("outline_will_appear_here") || "Your outline will appear here"}
                description={t("outline_empty_desc") || "Pick a topic or scripture on the left, or try one of these:"}
                examples={EXAMPLE_OUTLINES}
                onPick={(ex) => { setTopic(ex.topic); setScripture(ex.scripture); }}
                testId="outline-empty"
              />
            ) : (
              <motion.div {...fadeIn} className="space-y-3 text-sm" data-testid="outline-result">
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <Input
                      value={outline.title || ""}
                      onChange={(e) => setField("title", e.target.value)}
                      className="text-base font-semibold text-amber-300 border-0 bg-transparent px-0 h-auto py-1 focus-visible:bg-amber-500/5 focus-visible:px-2 focus-visible:rounded"
                      data-testid="outline-title-input"
                    />
                    <RegenerateButton
                      section="title"
                      context={outline}
                      onResult={(text) => { setOutline((o) => ({ ...o, title: text })); clearDirty("title"); }}
                      testId="regen-outline-title"
                      label=""
                      isDirty={isDirty("title")}
                      t={t}
                    />
                  </div>
                  <Input
                    value={outline.scripture_main || ""}
                    onChange={(e) => setField("scripture_main", e.target.value)}
                    className="text-xs text-slate-400 border-0 bg-transparent px-0 h-auto py-0.5 mt-0.5 focus-visible:bg-amber-500/5 focus-visible:px-2 focus-visible:rounded"
                    data-testid="outline-scripture-input"
                  />
                </div>

                <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase text-amber-400">{t("big_idea") || "Big Idea"}</span>
                    <RegenerateButton section="big_idea" context={outline}
                      onResult={(text) => { setOutline((o) => ({ ...o, big_idea: text })); clearDirty("big_idea"); }}
                      label="" testId="regen-big-idea" isDirty={isDirty("big_idea")} t={t} />
                  </div>
                  <Textarea
                    value={outline.big_idea || ""}
                    onChange={(e) => setField("big_idea", e.target.value)}
                    className="resize-none border-0 bg-transparent px-0 py-0 min-h-[40px] focus-visible:bg-amber-500/5 focus-visible:px-2 focus-visible:rounded"
                    data-testid="outline-big-idea-input"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-slate-300 text-xs uppercase">{t("introduction") || "Introduction"}</h4>
                    <RegenerateButton section="introduction" context={outline}
                      onResult={(text) => { setOutline((o) => ({ ...o, introduction: text })); clearDirty("introduction"); }}
                      label="" testId="regen-intro" isDirty={isDirty("introduction")} t={t} />
                  </div>
                  <Textarea
                    value={outline.introduction || ""}
                    onChange={(e) => setField("introduction", e.target.value)}
                    className="resize-none border-0 bg-transparent px-0 mt-1 min-h-[60px] text-slate-200 focus-visible:bg-amber-500/5 focus-visible:px-2 focus-visible:rounded"
                    data-testid="outline-intro-input"
                  />
                </div>

                {(outline.points || []).map((pt, idx) => {
                  // A point is dirty if any of its sub-fields have been edited.
                  const pointDirty = isDirty("heading", idx) || isDirty("scripture", idx)
                    || isDirty("explanation", idx) || isDirty("illustration", idx);
                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="border-l-2 border-amber-500/40 pl-3 space-y-1.5"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <Input
                          value={pt.heading || ""}
                          onChange={(e) => setPointField(idx, "heading", e.target.value)}
                          className="font-semibold border-0 bg-transparent px-0 h-auto py-0.5 focus-visible:bg-amber-500/5 focus-visible:px-2 focus-visible:rounded"
                          data-testid={`outline-point-heading-${idx}`}
                        />
                        <RegenerateButton section="point" context={outline} pointIndex={idx}
                          onResult={(text) => {
                            setOutline((o) => {
                              const next = [...o.points];
                              next[idx] = { ...next[idx], heading: text };
                              return { ...o, points: next };
                            });
                            clearDirty("heading", idx);
                          }} label="" testId={`regen-point-${idx}`}
                          isDirty={pointDirty} t={t} />
                      </div>
                      <Input
                        value={pt.scripture || ""}
                        onChange={(e) => setPointField(idx, "scripture", e.target.value)}
                        className="text-xs text-amber-300 border-0 bg-transparent px-0 h-auto py-0.5 focus-visible:bg-amber-500/5 focus-visible:px-2 focus-visible:rounded"
                        data-testid={`outline-point-scripture-${idx}`}
                        placeholder="(scripture reference)"
                      />
                      <div className="flex items-start justify-between gap-2">
                        <Textarea
                          value={pt.explanation || ""}
                          onChange={(e) => setPointField(idx, "explanation", e.target.value)}
                          className="flex-1 resize-none border-0 bg-transparent px-0 min-h-[50px] text-slate-200 focus-visible:bg-amber-500/5 focus-visible:px-2 focus-visible:rounded"
                          data-testid={`outline-point-explanation-${idx}`}
                        />
                        <RegenerateButton section="explanation" context={outline} pointIndex={idx}
                          onResult={(text) => {
                            setOutline((o) => {
                              const next = [...o.points];
                              next[idx] = { ...next[idx], explanation: text };
                              return { ...o, points: next };
                            });
                            clearDirty("explanation", idx);
                          }} label="" testId={`regen-explanation-${idx}`}
                          isDirty={isDirty("explanation", idx)} t={t} />
                      </div>
                      {(pt.illustration || pt.illustration === "") && (
                        <div className="flex items-start justify-between gap-2 pt-1 border-t border-slate-800/50">
                          <Textarea
                            value={pt.illustration || ""}
                            onChange={(e) => setPointField(idx, "illustration", e.target.value)}
                            className="flex-1 resize-none border-0 bg-transparent px-0 min-h-[40px] italic text-slate-400 text-xs focus-visible:bg-amber-500/5 focus-visible:px-2 focus-visible:rounded"
                            placeholder="(illustration)"
                            data-testid={`outline-point-illustration-${idx}`}
                          />
                          <RegenerateButton section="illustration" context={outline} pointIndex={idx}
                            onResult={(text) => {
                              setOutline((o) => {
                                const next = [...o.points];
                                next[idx] = { ...next[idx], illustration: text };
                                return { ...o, points: next };
                              });
                              clearDirty("illustration", idx);
                            }} label="" testId={`regen-illustration-${idx}`}
                            isDirty={isDirty("illustration", idx)} t={t} />
                        </div>
                      )}
                    </motion.div>
                  );
                })}

                <div>
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-slate-300 text-xs uppercase">{t("application") || "Application"}</h4>
                    <RegenerateButton section="application" context={outline}
                      onResult={(text) => { setOutline((o) => ({ ...o, application: text })); clearDirty("application"); }}
                      label="" testId="regen-application" isDirty={isDirty("application")} t={t} />
                  </div>
                  <Textarea
                    value={outline.application || ""}
                    onChange={(e) => setField("application", e.target.value)}
                    className="resize-none border-0 bg-transparent px-0 mt-1 min-h-[60px] text-slate-200 focus-visible:bg-amber-500/5 focus-visible:px-2 focus-visible:rounded"
                    data-testid="outline-application-input"
                  />
                </div>

                <div className="p-2 rounded bg-slate-800/60">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase text-slate-400">{t("call_to_action") || "Call to Action"}</span>
                    <RegenerateButton section="call_to_action" context={outline}
                      onResult={(text) => { setOutline((o) => ({ ...o, call_to_action: text })); clearDirty("call_to_action"); }}
                      label="" testId="regen-cta" isDirty={isDirty("call_to_action")} t={t} />
                  </div>
                  <Textarea
                    value={outline.call_to_action || ""}
                    onChange={(e) => setField("call_to_action", e.target.value)}
                    className="resize-none border-0 bg-transparent px-0 mt-1 min-h-[40px] text-slate-100 focus-visible:bg-amber-500/5 focus-visible:px-2 focus-visible:rounded"
                    data-testid="outline-cta-input"
                  />
                </div>
              </motion.div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
